import { XCircle, CheckCircle } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import type { AdsMode } from "./AdsModeSelector";

const venteContent = {
  title: "Pendant que vous êtes en visite…",
  withoutSteps: [
    "Vous êtes en estimation.",
    "Un vendeur appelle.",
    "Messagerie.",
    "Il appelle le concurrent.",
    "Le concurrent décroche.",
    "Mandat perdu.",
  ],
  withSteps: [
    "Appel pris immédiatement.",
    "Besoin qualifié.",
    "Infos envoyées par SMS et e-mail.",
    "Rappel préparé.",
  ],
  conclusion: "Le mandat se signe ailleurs.",
  conclusionHighlight: " Sauf si quelqu'un répond.",
};

const locationContent = {
  title: "Vous publiez un bien. 35 appels tombent.",
  withoutSteps: [
    "Annonce publiée.",
    "35 appels en quelques heures.",
    "La moitié non solvables.",
    "Journée au téléphone.",
    "Les bons dossiers attendent.",
    "Vacance prolongée.",
  ],
  withSteps: [
    "Chaque appel pris.",
    "Solvabilité vérifiée.",
    "Dossiers triés automatiquement.",
    "Bien loué plus vite.",
  ],
  conclusion: "Immovox récupère les infos utiles,",
  conclusionHighlight: " vous gérez les meilleurs profils.",
};

const containerVariants = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.12 } },
};

const cardVariants = {
  hidden: { opacity: 0, y: 40 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: [0.16, 1, 0.3, 1] as const } },
};

const stepVariants = {
  hidden: { opacity: 0, x: -16 },
  visible: { opacity: 1, x: 0, transition: { duration: 0.35, ease: "easeOut" as const } },
};

const AdsCompetitiveStory = ({ mode }: { mode: AdsMode }) => {
  const c = mode === "vente" ? venteContent : locationContent;

  return (
    <section className="section-padding bg-background">
      <div className="container mx-auto px-4">
        <AnimatePresence mode="wait">
          <motion.div
            key={mode}
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.3 }}
          >
            <div className="max-w-2xl mx-auto text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-semibold text-foreground">
                {c.title}
              </h2>
            </div>

            <motion.div
              className="max-w-4xl mx-auto grid md:grid-cols-2 gap-6 md:gap-8"
              variants={containerVariants}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.2 }}
            >
              {/* Sans Immovox */}
              <motion.div
                variants={cardVariants}
                className="rounded-2xl border border-destructive/20 bg-destructive/5 p-6 md:p-8"
              >
                <div className="flex items-center gap-2 mb-6">
                  <XCircle className="w-5 h-5 text-destructive" />
                  <h3 className="font-bold text-foreground text-lg">Sans Immovox</h3>
                </div>
                <motion.ol
                  className="space-y-4"
                  variants={containerVariants}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true, amount: 0.3 }}
                >
                  {c.withoutSteps.map((step, i) => (
                    <motion.li key={i} variants={stepVariants} className="flex items-start gap-3">
                      <span className="w-6 h-6 rounded-full bg-destructive/10 text-destructive flex items-center justify-center shrink-0 text-xs font-bold mt-0.5">
                        {i + 1}
                      </span>
                      <p className="text-foreground/80 leading-relaxed">{step}</p>
                    </motion.li>
                  ))}
                </motion.ol>
              </motion.div>

              {/* Avec Immovox */}
              <motion.div
                variants={cardVariants}
                className="rounded-2xl border border-primary/20 bg-primary/5 p-6 md:p-8"
              >
                <div className="flex items-center gap-2 mb-6">
                  <CheckCircle className="w-5 h-5 text-primary" />
                  <h3 className="font-bold text-foreground text-lg">Avec Immovox</h3>
                </div>
                <motion.ol
                  className="space-y-4"
                  variants={containerVariants}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true, amount: 0.3 }}
                >
                  {c.withSteps.map((step, i) => (
                    <motion.li key={i} variants={stepVariants} className="flex items-start gap-3">
                      <span className="w-6 h-6 rounded-full bg-primary/10 text-primary flex items-center justify-center shrink-0 text-xs font-bold mt-0.5">
                        {i + 1}
                      </span>
                      <p className="text-foreground/80 leading-relaxed">{step}</p>
                    </motion.li>
                  ))}
                </motion.ol>
              </motion.div>
            </motion.div>

            <div className="max-w-2xl mx-auto text-center mt-10">
              <p className="text-xl font-semibold text-foreground">
                {c.conclusion}
                <span className="text-primary">{c.conclusionHighlight}</span>
              </p>
            </div>
          </motion.div>
        </AnimatePresence>
      </div>
    </section>
  );
};

export default AdsCompetitiveStory;
